import {getSourceFiles, SourceFile} from '_logics/LFCSourceUtil';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {Box, Typography, List, ListItemButton, Collapse, CircularProgress} from '@mui/material';
import React, {useCallback, useEffect, useRef, useState} from 'react';

type FileTreeProps = {
  files: SourceFile[];
  selectedPath: string | null;
  onSelect: (file: SourceFile) => void;
  deviceName: string;
  commitId?: string | null;
};

type TreeNode = SourceFile & {
  children?: TreeNode[];
  isLoaded?: boolean;
};

// 個別のツリーアイテムコンポーネント
const TreeItemRow = React.memo(
  ({
    node,
    depth,
    isSelected,
    isExpanded,
    isLoading,
    onToggle,
    onSelect
  }: {
    node: TreeNode;
    depth: number;
    isSelected: boolean;
    isExpanded: boolean;
    isLoading: boolean;
    onToggle: (path: string) => void;
    onSelect: (node: TreeNode) => void;
  }) => {
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (node.isFolder) {
          onToggle(node.path);
        } else {
          onSelect(node);
        }
      },
      [node, onToggle, onSelect]
    );

    return (
      <ListItemButton
        onClick={handleClick}
        selected={isSelected}
        sx={{
          pl: 1 + depth * 2,
          py: 0.25,
          minHeight: 32
        }}
      >
        {node.isFolder ? (
          isExpanded ? (
            <ExpandMoreIcon fontSize="small" sx={{mr: 0.5}} />
          ) : (
            <ChevronRightIcon fontSize="small" sx={{mr: 0.5}} />
          )
        ) : (
          <Box sx={{width: 24, mr: 0.5}} />
        )}
        {node.isFolder ? (
          <FolderIcon fontSize="small" sx={{color: 'warning.main', mr: 1}} />
        ) : (
          <InsertDriveFileIcon fontSize="small" sx={{color: 'action.active', mr: 1}} />
        )}
        <Typography
          variant="body2"
          sx={{
            fontWeight: node.isFolder ? 'medium' : 'normal',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {node.name}
        </Typography>
        {isLoading && <CircularProgress size={14} sx={{ml: 1}} />}
      </ListItemButton>
    );
  }
);

TreeItemRow.displayName = 'TreeItemRow';

// 再帰的なツリーノードコンポーネント
const TreeNodeComponent = React.memo(
  ({
    node,
    depth,
    selectedPath,
    expandedPaths,
    loadingPaths,
    onToggle,
    onSelect,
    sortedChildren
  }: {
    node: TreeNode;
    depth: number;
    selectedPath: string | null;
    expandedPaths: Set<string>;
    loadingPaths: Set<string>;
    onToggle: (path: string) => void;
    onSelect: (node: TreeNode) => void;
    sortedChildren: (children: TreeNode[]) => TreeNode[];
  }) => {
    const isExpanded = expandedPaths.has(node.path);
    const isSelected = selectedPath === node.path;
    const isLoading = loadingPaths.has(node.path);

    return (
      <>
        <TreeItemRow
          node={node}
          depth={depth}
          isSelected={isSelected}
          isExpanded={isExpanded}
          isLoading={isLoading}
          onToggle={onToggle}
          onSelect={onSelect}
        />
        {node.isFolder && (
          <Collapse in={isExpanded} timeout={0} unmountOnExit>
            {node.children && node.children.length > 0 ? (
              sortedChildren(node.children).map(child => (
                <TreeNodeComponent
                  key={child.path}
                  node={child}
                  depth={depth + 1}
                  selectedPath={selectedPath}
                  expandedPaths={expandedPaths}
                  loadingPaths={loadingPaths}
                  onToggle={onToggle}
                  onSelect={onSelect}
                  sortedChildren={sortedChildren}
                />
              ))
            ) : node.isLoaded ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{pl: 2 + (depth + 1) * 2, py: 0.5}}
              >
                （空）
              </Typography>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{pl: 2 + (depth + 1) * 2, py: 0.5}}
              >
                読み込み中...
              </Typography>
            )}
          </Collapse>
        )}
      </>
    );
  }
);

TreeNodeComponent.displayName = 'TreeNodeComponent';

/**
 * ファイルツリー表示コンポーネント
 * フォルダを展開して階層表示
 */
const FileTree = ({files, selectedPath, onSelect, deviceName, commitId}: FileTreeProps) => {
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [loadingPaths, setLoadingPaths] = useState<Set<string>>(new Set());

  // ノード検索用のMapをキャッシュ
  const nodeMapRef = useRef<Map<string, TreeNode>>(new Map());

  // フォルダ読み込みのAbortController管理
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // filesが変更されたらツリーをリセット
  useEffect(() => {
    // 進行中のリクエストをすべてキャンセル
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current.clear();

    const newTreeData = files.map(f => ({
      ...f,
      children: f.isFolder ? [] : undefined,
      isLoaded: false
    }));

    setTreeData(newTreeData);
    setExpandedPaths(new Set());
    setLoadingPaths(new Set());

    // nodeMapを更新
    const newMap = new Map<string, TreeNode>();
    const addToMap = (nodes: TreeNode[]) => {
      nodes.forEach(node => {
        newMap.set(node.path, node);
        if (node.children) {
          addToMap(node.children);
        }
      });
    };
    addToMap(newTreeData);
    nodeMapRef.current = newMap;
  }, [files]);

  // 絶対パスからAPIに渡す相対パスを計算
  const toRelativePath = useCallback(
    (absolutePath: string): string => {
      const prefix = `devices/${deviceName}/`;
      if (absolutePath.startsWith(prefix)) {
        return absolutePath.substring(prefix.length);
      }
      return absolutePath;
    },
    [deviceName]
  );

  // フォルダの子要素を取得
  const loadChildren = useCallback(
    async (folderPath: string) => {
      // 既存のリクエストがあればキャンセル
      const existingController = abortControllersRef.current.get(folderPath);
      if (existingController) {
        existingController.abort();
      }

      // 新しいAbortControllerを作成
      const abortController = new AbortController();
      abortControllersRef.current.set(folderPath, abortController);

      setLoadingPaths(prev => new Set(prev).add(folderPath));

      try {
        const relativePath = toRelativePath(folderPath);
        const result = await getSourceFiles(
          deviceName,
          relativePath,
          commitId || undefined,
          abortController.signal
        );

        // キャンセルされていないか確認
        if (abortController.signal.aborted) return;

        const newChildren = result.files.map(f => ({
          ...f,
          children: f.isFolder ? [] : undefined,
          isLoaded: false
        }));

        setTreeData(prevData => {
          const updateNode = (nodes: TreeNode[]): TreeNode[] => {
            return nodes.map(n => {
              if (n.path === folderPath) {
                return {
                  ...n,
                  children: newChildren,
                  isLoaded: true
                };
              }
              if (n.children && n.children.length > 0) {
                return {...n, children: updateNode(n.children)};
              }
              return n;
            });
          };
          const updatedData = updateNode(prevData);

          // nodeMapを更新
          const newMap = new Map(nodeMapRef.current);
          newChildren.forEach(child => newMap.set(child.path, child));
          const parentNode = newMap.get(folderPath);
          if (parentNode) {
            newMap.set(folderPath, {...parentNode, children: newChildren, isLoaded: true});
          }
          nodeMapRef.current = newMap;

          return updatedData;
        });
      } catch (error) {
        // キャンセルされた場合は何もしない
        if (error instanceof Error && error.name === 'CanceledError') {
          return;
        }
        console.error('フォルダ読み込みエラー:', error);
      } finally {
        // このリクエストがまだアクティブな場合のみクリーンアップ
        if (abortControllersRef.current.get(folderPath) === abortController) {
          abortControllersRef.current.delete(folderPath);
          setLoadingPaths(prev => {
            const next = new Set(prev);
            next.delete(folderPath);
            return next;
          });
        }
      }
    },
    [deviceName, commitId, toRelativePath]
  );

  // 展開/格納トグル
  const handleToggle = useCallback(
    (path: string) => {
      setExpandedPaths(prev => {
        const next = new Set(prev);
        if (next.has(path)) {
          next.delete(path);
        } else {
          next.add(path);
          // 展開時に未ロードならロード
          const node = nodeMapRef.current.get(path);
          if (node && node.isFolder && !node.isLoaded) {
            loadChildren(path);
          }
        }
        return next;
      });
    },
    [loadChildren]
  );

  // ファイル選択
  const handleSelect = useCallback(
    (node: TreeNode) => {
      if (!node.isFolder) {
        onSelect(node);
      }
    },
    [onSelect]
  );

  // ソート関数
  const sortedChildren = useCallback((children: TreeNode[]): TreeNode[] => {
    return [...children].sort((a, b) => {
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
      return a.name.localeCompare(b.name);
    });
  }, []);

  if (files.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{p: 2}}>
        ファイルがありません
      </Typography>
    );
  }

  return (
    <List
      dense
      disablePadding
      sx={{
        height: '100%',
        overflow: 'auto',
        '& .MuiListItemButton-root': {
          borderRadius: 0
        }
      }}
    >
      {sortedChildren(treeData).map(node => (
        <TreeNodeComponent
          key={node.path}
          node={node}
          depth={0}
          selectedPath={selectedPath}
          expandedPaths={expandedPaths}
          loadingPaths={loadingPaths}
          onToggle={handleToggle}
          onSelect={handleSelect}
          sortedChildren={sortedChildren}
        />
      ))}
    </List>
  );
};

export default React.memo(FileTree);
